import React, { useState, useEffect } from 'react';
import {
  Plus,
  Upload,
  Download,
  Edit,
  Trash2,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Globe,
  FileText,
  CheckCircle2,
  X,
  Search,
  Check,
  Loader2,
  AlertCircle
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  fetchFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
  importFaqs,
  SupportFaq
} from '../../services/MasterAdmin/supportFaqService';

const categoryLabels: Record<string, string> = {
  doctor_consultation: 'Doctor Consultation',
  ambulance_booking: 'Ambulance Booking',
  health_blogs: 'Health Blogs',
  period_tracker: 'Period Tracker',
  medicine_order: 'Medicine Order',
  blood_request: 'Blood Request',
  refund_payments_medicine: 'Refund & Payments',
};

const SupportFaqsPage: React.FC = () => {
  const [faqs, setFaqs] = useState<SupportFaq[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Filters & Search
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // UI state
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [showFormModal, setShowFormModal] = useState(false);
  const [formModalMode, setFormModalMode] = useState<'add' | 'edit'>('add');
  const [activeFormTab, setActiveFormTab] = useState<'en' | 'hi' | 'mr'>('en');
  const [selectedFaq, setSelectedFaq] = useState<SupportFaq | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<SupportFaq | null>(null);

  // Excel Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
  const [importLoading, setImportLoading] = useState(false);

  // Manual FAQ fields
  const [form, setForm] = useState({
    category: 'doctor_consultation',
    question_en: '',
    question_hi: '',
    question_mr: '',
    answer_en: '',
    answer_hi: '',
    answer_mr: '',
    action_type: '',
    action_target: '',
    action_label: '',
    display_order: 0,
    is_active: true,
  });

  const [previewActive, setPreviewActive] = useState(false);

  useEffect(() => {
    loadFaqs();
  }, []);

  const loadFaqs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchFaqs();
      if (response.success && response.data) {
        setFaqs(response.data);
        // Expand all categories by default
        const initialExpanded: Record<string, boolean> = {};
        response.data.forEach((faq) => {
          initialExpanded[faq.category] = true;
        });
        setExpandedCategories(initialExpanded);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load FAQ list.');
    } finally {
      setLoading(false);
    }
  };

  const showToastMsg = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Manual CRUD Handlers
  const handleOpenAddModal = () => {
    setFormModalMode('add');
    setSelectedFaq(null);
    setForm({
      category: 'doctor_consultation',
      question_en: '',
      question_hi: '',
      question_mr: '',
      answer_en: '',
      answer_hi: '',
      answer_mr: '',
      action_type: '',
      action_target: '',
      action_label: '',
      display_order: faqs.length + 1,
      is_active: true,
    });
    setActiveFormTab('en');
    setPreviewActive(false);
    setShowFormModal(true);
  };

  const handleOpenEditModal = (faq: SupportFaq) => {
    setFormModalMode('edit');
    setSelectedFaq(faq);
    setForm({
      category: faq.category,
      question_en: faq.question_en || '',
      question_hi: faq.question_hi || '',
      question_mr: faq.question_mr || '',
      answer_en: faq.answer_en || '',
      answer_hi: faq.answer_hi || '',
      answer_mr: faq.answer_mr || '',
      action_type: faq.action_type || '',
      action_target: faq.action_target || '',
      action_label: faq.action_label || '',
      display_order: faq.display_order,
      is_active: faq.is_active,
    });
    setActiveFormTab('en');
    setPreviewActive(false);
    setShowFormModal(true);
  };

  const handleOpenDeleteModal = (faq: SupportFaq) => {
    setFaqToDelete(faq);
    setShowDeleteModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSaveFaq = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.question_en.trim() || !form.answer_en.trim()) {
      showToastMsg('English Question and Answer are required.');
      return;
    }

    setSaving(true);
    try {
      const cleanedForm = {
        ...form,
        question_hi: form.question_hi.trim() || null,
        question_mr: form.question_mr.trim() || null,
        answer_hi: form.answer_hi.trim() || null,
        answer_mr: form.answer_mr.trim() || null,
        action_type: form.action_type.trim() || null,
        action_target: form.action_target.trim() || null,
        action_label: form.action_label.trim() || null,
      };

      if (formModalMode === 'add') {
        const response = await createFaq(cleanedForm);
        if (response.success) {
          showToastMsg('FAQ created successfully.');
          loadFaqs();
          setShowFormModal(false);
        }
      } else if (formModalMode === 'edit' && selectedFaq) {
        const response = await updateFaq(selectedFaq.id, cleanedForm);
        if (response.success) {
          showToastMsg('FAQ updated successfully.');
          loadFaqs();
          setShowFormModal(false);
        }
      }
    } catch (err: any) {
      console.error(err);
      showToastMsg(err?.response?.data?.message || 'Error occurred while saving FAQ.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;
    try {
      const response = await deleteFaq(faqToDelete.id);
      if (response.success) {
        showToastMsg('FAQ deleted successfully.');
        loadFaqs();
        setShowDeleteModal(false);
        setFaqToDelete(null);
      }
    } catch (err: any) {
      console.error(err);
      showToastMsg(err?.response?.data?.message || 'Failed to delete FAQ.');
    }
  };

  // CSV/Excel Seeding template downloader
  const downloadTemplate = () => {
    const headers = [
      'category',
      'question_en',
      'question_hi',
      'question_mr',
      'answer_en',
      'answer_hi',
      'answer_mr',
      'action_type',
      'action_target',
      'action_label',
      'display_order',
      'is_active',
    ];
    const sampleRow = [
      'doctor_consultation',
      'How do I book a doctor appointment?',
      'मैं डॉक्टर अपॉइंटमेंट कैसे बुक करूं?',
      'मी डॉक्टर अपॉइंटमेंट कशी बुक करू?',
      '**Instant Call**: Click "Instant Call" ➡️ Select specialty ➡️ Send request.',
      '**तुरंत कॉल**: "तुरंत कॉल" चुनें ➡️ विशेषता चुनें ➡️ अनुरोध भेजें।',
      '**त्वरित कॉल**: "त्वरित कॉल" निवडा ➡️ तज्ञ निवडा ➡️ विनंती पाठवा।',
      'NAVIGATE',
      'DOCTOR_LISTING_PAGE',
      'Book Now',
      '1',
      'true',
    ];
    const csvContent = [headers.join(','), sampleRow.map((v) => `"${v.replace(/"/g, '""')}"`).join(',')].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'vedika_support_faq_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export current FAQs as CSV
  const exportCurrentFaqs = () => {
    const headers = [
      'category',
      'question_en',
      'question_hi',
      'question_mr',
      'answer_en',
      'answer_hi',
      'answer_mr',
      'action_type',
      'action_target',
      'action_label',
      'display_order',
      'is_active',
    ];

    const rows = faqs.map((faq) => [
      faq.category,
      faq.question_en,
      faq.question_hi || '',
      faq.question_mr || '',
      faq.answer_en,
      faq.answer_hi || '',
      faq.answer_mr || '',
      faq.action_type || '',
      faq.action_target || '',
      faq.action_label || '',
      faq.display_order.toString(),
      faq.is_active ? 'true' : 'false',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'vedika_support_faqs_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
    }
  };

  const handleImportExcel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importFile) return;

    setImportLoading(true);
    try {
      const response = await importFaqs(importFile, importMode);
      if (response.success) {
        showToastMsg(`Successfully imported ${response.count} FAQs.`);
        loadFaqs();
        setShowImportModal(false);
        setImportFile(null);
      }
    } catch (err: any) {
      console.error(err);
      showToastMsg(err?.response?.data?.message || 'Failed to import FAQ sheet.');
    } finally {
      setImportLoading(false);
    }
  };

  // Grouped and filtered FAQs
  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = categoryFilter === 'all' || faq.category === categoryFilter;
    const matchesSearch =
      searchQuery.trim() === '' ||
      [
        faq.question_en,
        faq.question_hi,
        faq.question_mr,
        faq.answer_en,
        faq.answer_hi,
        faq.answer_mr,
        faq.category,
      ]
        .filter(Boolean)
        .some((text) => text!.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const groupedFaqs = filteredFaqs.reduce<Record<string, SupportFaq[]>>((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="w-full space-y-6 p-4 md:p-6 transition-colors duration-200">
      {/* Header section */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Support FAQs Manager</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Manage multilingual static FAQs rendered in the patient mobile and web apps.
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5 sm:self-center">
          <button
            onClick={exportCurrentFaqs}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 rounded-lg shadow-sm text-sm font-medium transition-all"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 rounded-lg shadow-sm text-sm font-medium transition-all"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Import
          </button>
          <button
            onClick={handleOpenAddModal}
            className="flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg shadow-sm text-sm font-medium transition-all"
          >
            <Plus className="w-4 h-4 mr-2" /> Add FAQ
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-gray-800 dark:bg-gray-100 text-white dark:text-gray-900 px-4 py-2.5 rounded-lg shadow-xl text-sm font-medium animate-fade-in flex items-center gap-2 border border-gray-700 dark:border-gray-200">
          <Check className="w-4 h-4 text-green-500" />
          {toast}
        </div>
      )}

      {/* Filter and search control bar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="relative flex-1 max-w-md">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
            <Search className="w-4 h-4" />
          </span>
          <input
            type="text"
            placeholder="Search FAQs (En / Hi / Mr)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
          />
        </div>

        <div className="flex items-center gap-2.5">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Category:</span>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
          >
            <option value="all">All Categories</option>
            {Object.keys(categoryLabels).map((cat) => (
              <option key={cat} value={cat}>
                {categoryLabels[cat]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* FAQs Categorized Accordions */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-3">
          <Loader2 className="w-9 h-9 text-primary-500 animate-spin" />
          <span className="text-gray-500 dark:text-gray-400 text-sm">Fetching FAQ records...</span>
        </div>
      ) : Object.keys(groupedFaqs).length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/80">
          <HelpCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No FAQs found matching the filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.keys(groupedFaqs).map((category) => {
            const list = groupedFaqs[category];
            const isExpanded = expandedCategories[category] !== false;

            return (
              <div
                key={category}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                {/* Accordion header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-50 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400">
                      <HelpCircle className="w-4 h-4" />
                    </span>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                        {categoryLabels[category] || category}
                      </h3>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{list.length} Questions</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {/* Collapsible item list */}
                {isExpanded && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {list.map((faq) => (
                      <div
                        key={faq.id}
                        className="p-5 hover:bg-gray-50/50 dark:hover:bg-gray-700/10 transition flex flex-col md:flex-row md:items-start md:justify-between gap-4"
                      >
                        <div className="space-y-2 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                              Order: {faq.display_order}
                            </span>
                            {faq.is_active ? (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded-full border border-green-200 dark:border-green-800/40 flex items-center gap-0.5">
                                <Check className="w-2.5 h-2.5" /> Active
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 rounded-full border border-gray-200 dark:border-gray-700">
                                Inactive
                              </span>
                            )}
                            {faq.action_type && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-violet-50 dark:bg-violet-950/20 text-violet-700 dark:text-violet-400 rounded border border-violet-100 dark:border-violet-800/30">
                                Redirect: {faq.action_label || faq.action_target}
                              </span>
                            )}
                            {/* Lang flags */}
                            <div className="flex items-center gap-1.5 ml-2">
                              <span
                                className={`text-[10px] font-medium px-1 rounded ${
                                  faq.question_hi
                                    ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-300 dark:text-gray-600'
                                }`}
                                title={faq.question_hi ? 'Hindi version available' : 'Hindi missing'}
                              >
                                HI
                              </span>
                              <span
                                className={`text-[10px] font-medium px-1 rounded ${
                                  faq.question_mr
                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-300 dark:text-gray-600'
                                }`}
                                title={faq.question_mr ? 'Marathi version available' : 'Marathi missing'}
                              >
                                MR
                              </span>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                              {faq.question_en}
                            </h4>
                            {faq.question_hi && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium text-[10px] uppercase text-blue-500 mr-1">Hi:</span>
                                {faq.question_hi}
                              </p>
                            )}
                            {faq.question_mr && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium text-[10px] uppercase text-emerald-500 mr-1">Mr:</span>
                                {faq.question_mr}
                              </p>
                            )}
                          </div>

                          <div className="pt-1.5 border-t border-dashed border-gray-100 dark:border-gray-700/60 max-w-4xl">
                            <div className="text-xs text-gray-600 dark:text-gray-300 prose prose-sm dark:prose-invert">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{faq.answer_en}</ReactMarkdown>
                            </div>
                          </div>
                        </div>

                        {/* Card controls */}
                        <div className="flex md:flex-col justify-end items-center gap-1.5 md:self-start">
                          <button
                            onClick={() => handleOpenEditModal(faq)}
                            className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Edit FAQ"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(faq)}
                            className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Delete FAQ"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Manual FAQ Modal (Add/Edit) */}
      {showFormModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden animate-fade-in max-h-[92dvh] flex flex-col border border-gray-100 dark:border-gray-800">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-500 dark:from-primary-700 dark:to-primary-600 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {formModalMode === 'add' ? 'Add Support FAQ' : 'Edit Support FAQ'}
              </h3>
              <button
                type="button"
                onClick={() => setShowFormModal(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal scroll area */}
            <form onSubmit={handleSaveFaq} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    FAQ Category
                  </label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                  >
                    {Object.keys(categoryLabels).map((cat) => (
                      <option key={cat} value={cat}>
                        {categoryLabels[cat]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="display_order"
                    value={form.display_order}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                  />
                </div>
              </div>

              {/* Language Tabs */}
              <div className="space-y-4">
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                  {(['en', 'hi', 'mr'] as const).map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => {
                        setActiveFormTab(lang);
                        setPreviewActive(false);
                      }}
                      className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
                        activeFormTab === lang
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50/10'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5" />
                        {lang === 'en' ? 'English' : lang === 'hi' ? 'Hindi (हिंदी)' : 'Marathi (मराठी)'}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Localized inputs */}
                <div className="bg-gray-50/50 dark:bg-gray-800/30 p-4 rounded-xl border border-gray-100 dark:border-gray-800 space-y-4">
                  {activeFormTab === 'en' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          Question (English) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="question_en"
                          value={form.question_en}
                          onChange={handleInputChange}
                          placeholder="e.g. How do I request a refund?"
                          required
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Answer (English Markdown) <span className="text-red-500">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setPreviewActive(!previewActive)}
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                          >
                            {previewActive ? 'Hide Preview' : 'Show Markdown Preview'}
                          </button>
                        </div>
                        {previewActive ? (
                          <div className="w-full min-h-[160px] p-4 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg overflow-y-auto text-sm text-gray-800 dark:text-gray-200 prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.answer_en || '*Empty Answer*'}</ReactMarkdown>
                          </div>
                        ) : (
                          <textarea
                            name="answer_en"
                            value={form.answer_en}
                            onChange={handleInputChange}
                            rows={6}
                            placeholder="Write answer. Markdown supported (e.g. **bold**, - bullet list)."
                            required
                            className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none font-mono text-sm transition"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {activeFormTab === 'hi' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          Question (Hindi)
                        </label>
                        <input
                          type="text"
                          name="question_hi"
                          value={form.question_hi}
                          onChange={handleInputChange}
                          placeholder="प्रश्न हिंदी में"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Answer (Hindi Markdown)
                          </label>
                          <button
                            type="button"
                            onClick={() => setPreviewActive(!previewActive)}
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                          >
                            {previewActive ? 'Hide Preview' : 'Show Markdown Preview'}
                          </button>
                        </div>
                        {previewActive ? (
                          <div className="w-full min-h-[160px] p-4 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg overflow-y-auto text-sm text-gray-800 dark:text-gray-200 prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.answer_hi || '*Empty Answer*'}</ReactMarkdown>
                          </div>
                        ) : (
                          <textarea
                            name="answer_hi"
                            value={form.answer_hi}
                            onChange={handleInputChange}
                            rows={6}
                            placeholder="उत्तर हिंदी में (मार्काडाउन समर्थित)"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none font-mono text-sm transition"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {activeFormTab === 'mr' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          Question (Marathi)
                        </label>
                        <input
                          type="text"
                          name="question_mr"
                          value={form.question_mr}
                          onChange={handleInputChange}
                          placeholder="प्रश्न मराठीत"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Answer (Marathi Markdown)
                          </label>
                          <button
                            type="button"
                            onClick={() => setPreviewActive(!previewActive)}
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                          >
                            {previewActive ? 'Hide Preview' : 'Show Markdown Preview'}
                          </button>
                        </div>
                        {previewActive ? (
                          <div className="w-full min-h-[160px] p-4 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg overflow-y-auto text-sm text-gray-800 dark:text-gray-200 prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.answer_mr || '*Empty Answer*'}</ReactMarkdown>
                          </div>
                        ) : (
                          <textarea
                            name="answer_mr"
                            value={form.answer_mr}
                            onChange={handleInputChange}
                            rows={6}
                            placeholder="उत्तर मराठीत (मार्काडाउन समर्थित)"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none font-mono text-sm transition"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Redirection Setup */}
              <div className="bg-gray-50/50 dark:bg-gray-800/30 p-4 rounded-xl border border-gray-100 dark:border-gray-800/80 space-y-4">
                <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Optional Redirection (Action Button on Answer)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Action Type</label>
                    <select
                      name="action_type"
                      value={form.action_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none"
                    >
                      <option value="">None</option>
                      <option value="NAVIGATE">NAVIGATE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Action Target Page</label>
                    <input
                      type="text"
                      name="action_target"
                      value={form.action_target}
                      onChange={handleInputChange}
                      placeholder="e.g. CONTACT_US_PAGE"
                      className="w-full px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Action Label</label>
                    <input
                      type="text"
                      name="action_label"
                      value={form.action_label}
                      onChange={handleInputChange}
                      placeholder="e.g. Contact Support"
                      className="w-full px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Status toggles */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleCheckboxChange}
                  className="h-4.5 w-4.5 rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-500 transition"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Visible and active to app users
                </label>
              </div>

              {/* Submit panel */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white rounded-xl font-bold shadow-md transition disabled:opacity-50 flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving changes...
                  </>
                ) : formModalMode === 'add' ? (
                  'Create FAQ'
                ) : (
                  'Save Changes'
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete FAQ Modal */}
      {showDeleteModal && faqToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 text-center border border-gray-100 dark:border-gray-800 animate-fade-in">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-950/30 text-red-600 dark:text-red-400 mb-4">
              <Trash2 className="h-6.5 w-6.5" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Are you sure you want to permanently delete this FAQ from category <span className="font-semibold">{categoryLabels[faqToDelete.category]}</span>? This cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow transition"
              >
                Delete FAQ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Seeding Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-45 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm">
          <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 border border-gray-100 dark:border-gray-800 animate-fade-in">
            <button
              type="button"
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded transition"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
              <Upload className="w-5 h-5 text-primary-500" /> Excel Bulk Import
            </h3>

            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <p className="font-semibold">Import Guidelines:</p>
                <p>Use our standard template. Required columns: `category`, `question_en`, and `answer_en`.</p>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="font-bold underline text-blue-600 dark:text-blue-400 hover:text-blue-800"
                >
                  Download Sample CSV Template
                </button>
              </div>
            </div>

            <form onSubmit={handleImportExcel} className="space-y-4">
              {/* Drag/Drop area */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-950/40 text-center transition hover:bg-gray-50/80">
                <Upload className="w-9 h-9 text-gray-400 mb-2" />
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="excel_file_upload"
                />
                <label
                  htmlFor="excel_file_upload"
                  className="cursor-pointer text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {importFile ? 'Change spreadsheet file' : 'Click to select spreadsheet file'}
                </label>
                <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">Supports .xlsx, .xls, .csv</span>

                {importFile && (
                  <div className="mt-4 p-2 bg-primary-50 dark:bg-primary-950/20 rounded border border-primary-200 dark:border-primary-800/40 text-xs font-medium text-primary-700 dark:text-primary-400 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> {importFile.name}
                  </div>
                )}
              </div>

              {/* Import Mode options */}
              <div className="space-y-2">
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Import Mode
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="append"
                      checked={importMode === 'append'}
                      onChange={() => setImportMode('append')}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">Append Mode</span>
                      <span className="text-[10px] text-gray-400">Adds rows to existing data</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={() => setImportMode('replace')}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">Replace Mode</span>
                      <span className="text-[10px] text-gray-400">Truncates and replaces all FAQs</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Save */}
              <button
                type="submit"
                disabled={!importFile || importLoading}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold shadow transition disabled:opacity-50 flex items-center justify-center"
              >
                {importLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Importing spreadsheet...
                  </>
                ) : (
                  'Start Bulk Import'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportFaqsPage;
